<template>
  <div class="slot-calendar">
    <!-- Calendar Header -->
    <div class="slot-calendar__header">
      <BButton
        variant="outline-secondary"
        size="sm"
        @click="prevMonth"
      >
        <i class="fas fa-chevron-left" />
      </BButton>
      <h5 class="slot-calendar__title mb-0">
        {{ calendarTitle }}
      </h5>
      <BButton
        variant="outline-secondary"
        size="sm"
        @click="nextMonth"
      >
        <i class="fas fa-chevron-right" />
      </BButton>
    </div>

    <!-- Weekday Headers -->
    <div class="slot-calendar__weekdays">
      <div
        v-for="day in weekDays"
        :key="day"
        class="slot-calendar__weekday"
      >
        {{ day }}
      </div>
    </div>

    <!-- Calendar Grid -->
    <div class="slot-calendar__grid">
      <div
        v-for="(cell, idx) in calendarCells"
        :key="cell.key || idx"
        class="slot-calendar__cell"
        :class="{
          'slot-calendar__cell--other-month': !cell.isCurrentMonth,
          'slot-calendar__cell--today': cell.isToday,
          'slot-calendar__cell--selected': selectedDate === cell.date,
          'slot-calendar__cell--has-slots': cell.slots && cell.slots.length
        }"
        @click="selectDate(cell)"
      >
        <div class="slot-calendar__day-number">
          {{ cell.day }}
        </div>

        <!-- Show slot blocks inside cells (calendar view style) -->
        <div
          v-if="showSlotsInline && cell.slots && cell.slots.length"
          class="slot-calendar__inline-slots"
        >
          <div
            v-for="slot in getVisibleInlineSlots(cell)"
            :key="slot.id"
            class="slot-calendar__slot-block"
            :class="{
              'slot-calendar__slot-block--available': slot.is_available && slot.active_appointments_count === 0,
              'slot-calendar__slot-block--booked': slot.active_appointments_count > 0,
              'slot-calendar__slot-block--selected': selectedSlotId === slot.id
            }"
            @click.stop="selectSlot(slot)"
          >
            {{ formatInlineLabel(slot) }}
          </div>

          <button
            v-if="getHiddenCount(cell) > 0"
            type="button"
            class="slot-calendar__more"
            @click.stop="toggleMore(cell)"
          >
            +{{ getHiddenCount(cell) }} more
          </button>

          <transition name="slot-calendar-fade">
            <div
              v-if="showDesktopOverflowDropdown(cell)"
              class="slot-calendar__overflow-dropdown"
              @click.stop
            >
              <div class="slot-calendar__overflow-title">
                {{ formatSelectedDateByCell(cell.date) }}
              </div>
              <div
                v-for="slot in getHiddenSlots(cell)"
                :key="`overflow-${slot.id}`"
                class="slot-calendar__slot-block"
                :class="{
                  'slot-calendar__slot-block--available': slot.is_available && slot.active_appointments_count === 0,
                  'slot-calendar__slot-block--booked': slot.active_appointments_count > 0,
                  'slot-calendar__slot-block--selected': selectedSlotId === slot.id
                }"
                @click.stop="selectSlot(slot)"
              >
                {{ formatInlineLabel(slot) }}
              </div>
            </div>
          </transition>
        </div>

        <!-- Slot count indicators (simple view) -->
        <div
          v-else-if="cell.slots && cell.slots.length"
          class="slot-calendar__indicators"
        >
          <span
            v-if="cell.availableCount > 0"
            class="slot-calendar__indicator slot-calendar__indicator--available"
          >
            {{ cell.availableCount }}
          </span>
          <span
            v-if="cell.bookedCount > 0"
            class="slot-calendar__indicator slot-calendar__indicator--booked"
          >
            {{ cell.bookedCount }}
          </span>
        </div>
      </div>
    </div>

    <!-- Selected Day Detail Panel -->
    <div
      v-if="selectedDate && !showSlotsInline"
      class="slot-calendar__details"
    >
      <h6 class="slot-calendar__details-title">
        {{ formatSelectedDate }}
      </h6>

      <div
        v-if="selectedDateSlots.length"
        class="slot-calendar__slots"
      >
        <SlotCard
          v-for="slot in selectedDateSlots"
          :key="slot.id"
          :slot="slot"
          :selectable="selectable"
          :selected="selectedSlotId === slot.id"
          :show-actions="showActions"
          :disabled="selectable && (slot.active_appointments_count > 0 || !slot.is_available)"
          @select="selectSlot"
          @edit="$emit('edit', $event)"
          @delete="$emit('delete', $event)"
        />
      </div>

      <p
        v-else
        class="text-muted text-center"
      >
        No slots available for this date
      </p>

      <slot
        name="day-actions"
        :date="selectedDate"
      />
    </div>

    <!-- Mobile overflow modal -->
    <BModal
      v-model="showOverflowModal"
      size="lg"
      centered
      hide-footer
      title="Available Slots"
    >
      <div v-if="overflowDate">
        <h6 class="slot-calendar__details-title mb-3">
          {{ formatSelectedDateByCell(overflowDate) }}
        </h6>
      </div>

      <div
        v-if="overflowSlots.length"
        class="slot-calendar__slots"
      >
        <SlotCard
          v-for="slot in overflowSlots"
          :key="`mobile-overflow-${slot.id}`"
          :slot="slot"
          :selectable="selectable"
          :selected="selectedSlotId === slot.id"
          :show-actions="showActions"
          :disabled="selectable && (slot.active_appointments_count > 0 || !slot.is_available)"
          @select="selectSlot"
          @edit="$emit('edit', $event)"
          @delete="$emit('delete', $event)"
        />
      </div>
    </BModal>
  </div>
</template>

<script>
import SlotCard from './SlotCard.vue'

export default {
  name: 'SlotCalendar',

  components: { SlotCard },

  props: {
    slots: {
      type: Array,
      default: () => []
    },
    selectedSlotId: {
      type: [Number, String],
      default: null
    },
    selectable: {
      type: Boolean,
      default: false
    },
    showActions: {
      type: Boolean,
      default: false
    },
    showSlotsInline: {
      type: Boolean,
      default: false
    },
    maxVisibleSlots: {
      type: Number,
      default: 3
    },
    initialDate: {
      type: Date,
      default: () => new Date()
    }
  },

  data () {
    return {
      currentYear: this.initialDate.getFullYear(),
      currentMonth: this.initialDate.getMonth(),
      selectedDate: null,
      openOverflowDate: null,
      showOverflowModal: false,
      overflowDate: null,
      isMobileViewport: typeof window !== 'undefined' ? window.innerWidth < 768 : false,
      weekDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    }
  },

  computed: {
    calendarTitle () {
      const d = new Date(this.currentYear, this.currentMonth, 1)
      return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    },

    normalizedSlotsByDate () {
      const byDate = {}
      this.slots.forEach(slot => {
        const key = this.normalizeDateKey(slot.date)
        if (!key) return

        if (!byDate[key]) {
          byDate[key] = []
        }

        byDate[key].push({
          ...slot,
          date: key,
          is_available: slot.is_available ?? true,
          active_appointments_count: slot.active_appointments_count ?? 0
        })
      })

      Object.keys(byDate).forEach(key => {
        byDate[key].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
      })

      return byDate
    },

    calendarCells () {
      const firstDay = new Date(this.currentYear, this.currentMonth, 1)
      const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0)
      const startOffset = firstDay.getDay()
      const totalDays = lastDay.getDate()

      const cells = []
      const today = new Date()
      const todayStr = this.toDateString(today)

      // Previous month days
      const prevLastDay = new Date(this.currentYear, this.currentMonth, 0).getDate()
      for (let i = startOffset - 1; i >= 0; i--) {
        const d = prevLastDay - i
        const m = this.currentMonth - 1
        const y = m < 0 ? this.currentYear - 1 : this.currentYear
        const actualM = m < 0 ? 11 : m
        const dateStr = this.toDateString(new Date(y, actualM, d))
        cells.push(this.makeCellData(d, dateStr, false, dateStr === todayStr, i))
      }

      // Current month days
      for (let d = 1; d <= totalDays; d++) {
        const dateStr = this.toDateString(new Date(this.currentYear, this.currentMonth, d))
        cells.push(this.makeCellData(d, dateStr, true, dateStr === todayStr, d))
      }

      // Next month padding
      const remaining = 42 - cells.length
      for (let d = 1; d <= remaining; d++) {
        const m = this.currentMonth + 1
        const y = m > 11 ? this.currentYear + 1 : this.currentYear
        const actualM = m > 11 ? 0 : m
        const dateStr = this.toDateString(new Date(y, actualM, d))
        cells.push(this.makeCellData(d, dateStr, false, dateStr === todayStr, d + 50))
      }

      return cells
    },

    selectedDateSlots () {
      if (!this.selectedDate) return []
      return this.normalizedSlotsByDate[this.normalizeDateKey(this.selectedDate)] || []
    },

    formatSelectedDate () {
      if (!this.selectedDate) return ''
      const d = new Date(this.selectedDate + 'T00:00:00')
      return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    },

    overflowSlots () {
      if (!this.overflowDate) return []
      return this.normalizedSlotsByDate[this.normalizeDateKey(this.overflowDate)] || []
    }
  },

  watch: {
    currentMonth () {
      this.$emit('month-change', {
        year: this.currentYear,
        month: this.currentMonth,
        dateFrom: this.toDateString(new Date(this.currentYear, this.currentMonth, 1)),
        dateTo: this.toDateString(new Date(this.currentYear, this.currentMonth + 1, 0))
      })
    }
  },

  mounted () {
    document.addEventListener('click', this.onDocumentClick)
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onWindowResize)
    }

    // Emit initial month
    this.$emit('month-change', {
      year: this.currentYear,
      month: this.currentMonth,
      dateFrom: this.toDateString(new Date(this.currentYear, this.currentMonth, 1)),
      dateTo: this.toDateString(new Date(this.currentYear, this.currentMonth + 1, 0))
    })
  },

  beforeDestroy () {
    document.removeEventListener('click', this.onDocumentClick)
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onWindowResize)
    }
  },

  methods: {
    normalizeDateKey (value) {
      if (!value) return ''
      if (typeof value === 'string') {
        // Keep date-only strings unchanged to avoid timezone shifts.
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          return value
        }

        const parsed = new Date(value)
        if (!Number.isNaN(parsed.getTime())) {
          return this.toDateString(parsed)
        }

        if (value.length >= 10) {
          return value.slice(0, 10)
        }

        return ''
      }

      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      return this.toDateString(d)
    },

    toDateString (d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },

    makeCellData (day, dateStr, isCurrentMonth, isToday, index = 0) {
      const daySlots = this.normalizedSlotsByDate[dateStr] || []
      const available = daySlots.filter(s => s.is_available && s.active_appointments_count === 0).length
      const booked = daySlots.filter(s => s.active_appointments_count > 0).length
      return {
        key: `${dateStr}-${isCurrentMonth ? 'current' : 'adjacent'}-${index}`,
        day,
        date: dateStr,
        isCurrentMonth,
        isToday,
        slots: daySlots,
        slotCount: daySlots.length,
        availableCount: available,
        bookedCount: booked
      }
    },

    onWindowResize () {
      this.isMobileViewport = window.innerWidth < 768
      if (!this.isMobileViewport) {
        this.showOverflowModal = false
      }
    },

    onDocumentClick () {
      this.openOverflowDate = null
    },

    getVisibleInlineSlots (cell) {
      if (!cell?.slots?.length) return []
      const limit = this.isMobileViewport && this.selectable ? 2 : this.maxVisibleSlots
      return cell.slots.slice(0, limit)
    },

    getHiddenSlots (cell) {
      if (!cell?.slots?.length) return []
      const limit = this.isMobileViewport && this.selectable ? 2 : this.maxVisibleSlots
      return cell.slots.slice(limit)
    },

    getHiddenCount (cell) {
      return this.getHiddenSlots(cell).length
    },

    showDesktopOverflowDropdown (cell) {
      return !this.isMobileViewport && this.openOverflowDate === cell.date && this.getHiddenCount(cell) > 0
    },

    toggleMore (cell) {
      if (!cell || this.getHiddenCount(cell) === 0) return

      if (this.isMobileViewport) {
        this.overflowDate = cell.date
        this.showOverflowModal = true
        return
      }

      this.openOverflowDate = this.openOverflowDate === cell.date ? null : cell.date
    },

    formatSelectedDateByCell (dateStr) {
      const key = this.normalizeDateKey(dateStr)
      if (!key) return ''
      const d = new Date(`${key}T00:00:00`)
      return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    },

    formatInlineLabel (slot) {
      if (slot?.inline_label) return slot.inline_label
      if (this.selectable) {
        const start = this.formatTime(slot?.start_time)
        const end = this.formatTime(slot?.end_time)
        if (start && end) return `${start} - ${end}`
      }
      return this.formatTime(slot?.start_time)
    },

    formatTime (time) {
      if (!time) return ''
      const [hours, minutes] = time.substring(0, 5).split(':')
      const h = parseInt(hours, 10)
      const suffix = h >= 12 ? 'PM' : 'AM'
      const h12 = h % 12 || 12
      return `${h12}:${minutes} ${suffix}`
    },

    prevMonth () {
      if (this.currentMonth === 0) {
        this.currentMonth = 11
        this.currentYear--
      } else {
        this.currentMonth--
      }
      this.selectedDate = null
      this.openOverflowDate = null
    },

    nextMonth () {
      if (this.currentMonth === 11) {
        this.currentMonth = 0
        this.currentYear++
      } else {
        this.currentMonth++
      }
      this.selectedDate = null
      this.openOverflowDate = null
    },

    selectDate (cell) {
      if (!cell.isCurrentMonth) return
      this.selectedDate = cell.date
      this.openOverflowDate = null
      this.$emit('date-select', cell.date)
    },

    selectSlot (slot) {
      this.$emit('slot-click', slot)
      if (!this.selectable) return
      if (slot.active_appointments_count > 0 || !slot.is_available) return
      this.$emit('slot-select', slot)
    }
  }
}
</script>

<style lang="scss" scoped>
.slot-calendar {
  width: 100%;
  max-width: 100%;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    background: #f0fdf4;
    border-bottom: 1px solid #dcfce7;
  }

  &__title {
    font-weight: 600;
    color: #14532d;
  }

  &__weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    background: #fefce8;
    border-bottom: 1px solid #fde68a;
  }

  &__weekday {
    padding: 12px 8px;
    text-align: center;
    font-weight: 600;
    font-size: 0.85rem;
    color: #854d0e;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    width: 100%;
  }

  &__cell {
    min-width: 0;
    min-height: 90px;
    padding: 8px;
    border-right: 1px solid #e2e8f0;
    border-bottom: 1px solid #e2e8f0;
    cursor: pointer;
    transition: background 0.15s ease;

    &:nth-child(7n) {
      border-right: none;
    }

    &:hover {
      background: #f0fdf4;
    }

    &--other-month {
      background: #f8fafc;
      opacity: 0.5;
      cursor: default;

      &:hover {
        background: #f8fafc;
      }
    }

    &--today {
      background: #ecfdf5;

      .slot-calendar__day-number {
        background: var(--iq-primary-dark, #059669);
        color: #fff;
        border-radius: 50%;
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    &--selected {
      background: #fef9c3;
      border-color: var(--iq-secondary, #f59e0b);
    }

    &--has-slots {
      background: #fffbeb;
    }
  }

  &__day-number {
    font-weight: 600;
    font-size: 0.9rem;
    color: #334155;
    margin-bottom: 4px;
  }

  &__indicators {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
  }

  &__indicator {
    font-size: 0.7rem;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 600;

    &--available {
      background: #dcfce7;
      color: #166534;
    }

    &--booked {
      background: #fef3c7;
      color: #92400e;
    }
  }

  &__inline-slots {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 4px;
    position: relative;
  }

  &__slot-block {
    font-size: 0.7rem;
      padding: 3px 5px;
    border-radius: 3px;
    background: #e2e8f0;
    color: #475569;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    cursor: pointer;
    transition: all 0.15s ease;

    &--available {
      background: #dcfce7;
      color: #166534;

      &:hover {
        background: #bbf7d0;
      }
    }

    &--booked {
      background: #fef3c7;
      color: #92400e;
      cursor: not-allowed;
    }

    &--selected {
      background: var(--iq-primary-dark, #059669);
      color: #fff;
    }
  }

  &__more {
    border: 0;
    background: transparent;
    font-size: 0.65rem;
    color: #64748b;
    text-align: center;
    font-weight: 600;
    margin-top: 2px;
    padding: 2px 4px;
    border-radius: 4px;

    &:hover {
      background: #eef2ff;
      color: #1d4ed8;
    }
  }

  &__overflow-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    z-index: 20;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.15);
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 180px;
    overflow-y: auto;
  }

  &__overflow-title {
    font-size: 0.72rem;
    font-weight: 700;
    color: #334155;
    margin-bottom: 2px;
    padding-bottom: 6px;
    border-bottom: 1px solid #e2e8f0;
  }

  &__details {
    padding: 20px;
    border-top: 2px solid #dcfce7;
    background: #f0fdf4;
  }

  &__details-title {
    font-weight: 600;
    color: #14532d;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #dcfce7;
  }

  &__slots {
    max-height: 300px;
    overflow-y: auto;
  }
}

.slot-calendar,
.slot-calendar * {
  box-sizing: border-box;
}

@media (max-width: 767.98px) {
  .slot-calendar {
    &__header {
      padding: 10px 8px;
    }

    &__title {
      font-size: 1rem;
      text-align: center;
    }

    &__weekday {
      padding: 8px 2px;
      font-size: 0.72rem;
    }

    &__cell {
      min-height: 78px;
      padding: 4px;
    }

    &__day-number {
      font-size: 0.78rem;
      margin-bottom: 2px;
    }

    &__inline-slots {
      gap: 1px;
      margin-top: 2px;
    }

    &__slot-block {
      font-size: 0.62rem;
      padding: 2px 3px;
      border-radius: 4px;
    }

    &__more {
      font-size: 0.58rem;
      padding: 1px 2px;
    }

    &__details {
      padding: 14px 10px;
    }
  }
}

.slot-calendar-fade-enter-active,
.slot-calendar-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.slot-calendar-fade-enter,
.slot-calendar-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
